// src/components/institution/sections/LeadershipSection.jsx
import React from 'react';
import { Mail, Phone, Linkedin, User, Crown, Shield, Building2, Users, GraduationCap } from 'lucide-react';

export const LeadershipSection = ({ data, settings, styling, onPersonClick, staffMembers = [] }) => {
  
  // Define leadership positions to filter for
  const LEADERSHIP_POSITIONS = [
    // Principal/Senior Leadership
    'principal', 'vice principal', 'deputy principal', 'assistant principal',
    'headmaster', 'headmistress', 'head of school', 
    
    // Directors
    'director', 'executive director', 'academic director', 'managing director',
    'deputy director', 'assistant director',
    
    // Departmental Leadership  
    'department head', 'departmental head', 'head of department', 'hod',
    'faculty head', 'dean', 'associate dean', 'assistant dean',
    
    // Management
    'manager', 'senior manager', 'academic manager', 'operations manager',
    'program manager', 'curriculum manager',
    
    // Other Leadership Roles
    'coordinator', 'senior coordinator', 'lead', 'team lead',
    'supervisor', 'administrator', 'registrar'
  ];

  // Filter staff members for leadership positions
  const getLeadershipStaff = () => {
    if (!staffMembers || staffMembers.length === 0) {
      return [];
    }

    return staffMembers.filter(member => {
      // Check if role is admin or if position contains leadership keywords
      const isAdminRole = member.role === 'admin';
      
      const hasLeadershipPosition = member.position && 
        LEADERSHIP_POSITIONS.some(pos => 
          member.position.toLowerCase().includes(pos.toLowerCase())
        );
      
      const hasLeadershipTitle = member.name &&
        ['dr.', 'prof.', 'mr.', 'mrs.', 'ms.', 'principal', 'director'].some(title =>
          member.name.toLowerCase().includes(title.toLowerCase())
        );

      return isAdminRole || hasLeadershipPosition || hasLeadershipTitle;
    }).sort((a, b) => {
      // Sort by leadership hierarchy
      const getHierarchyOrder = (position) => {
        const pos = position?.toLowerCase() || '';
        if (pos.includes('principal') || pos.includes('headmaster') || pos.includes('headmistress')) return 1;
        if (pos.includes('deputy') || pos.includes('vice')) return 2;
        if (pos.includes('director')) return 3;
        if (pos.includes('dean')) return 4;
        if (pos.includes('head')) return 5;
        if (pos.includes('manager')) return 6;
        return 7;
      };
      
      return getHierarchyOrder(a.position) - getHierarchyOrder(b.position);
    });
  };

  // Get role-specific icon and styling
  const getRoleDisplay = (member) => {
    const position = member.position?.toLowerCase() || '';
    
    if (position.includes('principal') || position.includes('headmaster') || position.includes('headmistress')) {
      return {
        icon: Crown,
        bgColor: 'bg-purple-100',
        textColor: 'text-purple-700',
        borderColor: 'border-purple-200',
        label: 'School Leadership'
      };
    }
    
    if (position.includes('director')) {
      return {
        icon: Shield,
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-700',
        borderColor: 'border-blue-200',
        label: 'Executive Leadership'
      };
    }
    
    if (position.includes('dean') || position.includes('head')) {
      return {
        icon: GraduationCap,
        bgColor: 'bg-green-100',
        textColor: 'text-green-700',
        borderColor: 'border-green-200',
        label: 'Academic Leadership'
      };
    }
    
    if (position.includes('manager')) {
      return {
        icon: Building2,
        bgColor: 'bg-orange-100',
        textColor: 'text-orange-700',
        borderColor: 'border-orange-200',
        label: 'Management'
      };
    }
    
    // Default for other leadership roles
    return {
      icon: Users,
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-700',
      borderColor: 'border-gray-200',
      label: 'Leadership'
    };
  };

  const leadership = getLeadershipStaff();

  // Fallback to mock data if no staff members found
  const mockLeadership = [
    {
      id: 'mock-1',
      name: 'Dr. John Smith',
      position: 'Principal',
      role: 'admin',
      profileImage: null,
      email: 'principal@institution.edu',
      phone: '+27-123-456-7890',
      bio: 'Leading the institution with 20+ years of educational experience.',
      department: 'Administration',
      qualifications: 'PhD in Educational Leadership'
    },
    {
      id: 'mock-2', 
      name: 'Prof. Sarah Johnson',
      position: 'Vice Principal',
      role: 'admin',
      profileImage: null,
      email: 'vp@institution.edu',
      phone: '+27-123-456-7891',
      bio: 'Expert in curriculum development and academic excellence.',
      department: 'Academic Affairs',
      qualifications: 'PhD in Education'
    }
  ];

  const displayLeadership = leadership.length > 0 ? leadership : mockLeadership;

  return (
    <section className="py-16 px-4 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 
            className="text-2xl md:text-3xl font-bold mb-4"
            style={{ color: styling?.primaryColor || '#1e40af' }}
          >
            Leadership Team
          </h2>
          <div className="w-24 h-1 mx-auto mb-8" style={{ backgroundColor: styling?.primaryColor }} />
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Meet our experienced leadership team committed to educational excellence and institutional success.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayLeadership.map((person) => {
            const roleDisplay = getRoleDisplay(person);
            const RoleIcon = roleDisplay.icon;
            
            return (
              <div 
                key={person.id}
                className={`bg-white rounded-xl border-2 ${roleDisplay.borderColor} p-6 hover:shadow-xl transition-all duration-300 cursor-pointer group`}
                onClick={() => onPersonClick && onPersonClick(person)}
              >
                {/* Profile Image and Role Badge */}
                <div className="relative mb-4">
                  <div className="w-24 h-24 mx-auto mb-3 relative">
                    {person.profileImage || person.photo ? (
                      <img 
                        src={person.profileImage || person.photo} 
                        alt={person.name}
                        className="w-full h-full rounded-full object-cover border-4 border-white shadow-lg"
                      />
                    ) : (
                      <div className={`w-full h-full rounded-full ${roleDisplay.bgColor} flex items-center justify-center border-4 border-white shadow-lg`}>
                        <User className={`h-8 w-8 ${roleDisplay.textColor}`} />
                      </div>
                    )}
                    
                    {/* Role Badge */}
                    <div className={`absolute -bottom-1 -right-1 ${roleDisplay.bgColor} ${roleDisplay.textColor} rounded-full p-2 shadow-lg border-2 border-white`}>
                      <RoleIcon className="h-4 w-4" />
                    </div>
                  </div>
                  
                  {/* Role Label */}
                  <div className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${roleDisplay.bgColor} ${roleDisplay.textColor}`}>
                    {roleDisplay.label}
                  </div>
                </div>

                {/* Name and Position */}
                <div className="text-center mb-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors">
                    {person.name}
                  </h3>
                  <p className="text-blue-600 font-semibold mb-1">
                    {person.position}
                  </p>
                  {person.department && (
                    <p className="text-sm text-gray-500">
                      {person.department}
                    </p>
                  )}
                </div>

                {/* Bio */}
                {person.bio && (
                  <p className="text-gray-600 text-sm text-center mb-4 line-clamp-3">
                    {person.bio}
                  </p>
                )}

                {/* Qualifications */}
                {person.qualifications && (
                  <div className="mb-4">
                    <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                      <GraduationCap className="h-4 w-4" />
                      <span className="text-center">{person.qualifications}</span>
                    </div>
                  </div>
                )}

                {/* Contact Information */}
                <div className="space-y-2 pt-4 border-t border-gray-100">
                  {person.email && (
                    <div className="flex items-center justify-center gap-2">
                      <Mail className="h-4 w-4 text-gray-400" />
                      <a 
                        href={`mailto:${person.email}`} 
                        className="text-sm text-gray-600 hover:text-blue-600 transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {person.email}
                      </a>
                    </div>
                  )}
                  
                  {person.phone && (
                    <div className="flex items-center justify-center gap-2">
                      <Phone className="h-4 w-4 text-gray-400" />
                      <a 
                        href={`tel:${person.phone}`} 
                        className="text-sm text-gray-600 hover:text-blue-600 transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {person.phone}
                      </a>
                    </div>
                  )}
                </div>

                {/* Subjects for Educators in Leadership */}
                {person.subjects && person.subjects.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-xs text-gray-500 text-center mb-2">Teaching Areas:</p>
                    <div className="flex flex-wrap justify-center gap-1">
                      {person.subjects.slice(0, 3).map(subject => (
                        <span key={subject} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                          {subject}
                        </span>
                      ))}
                      {person.subjects.length > 3 && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                          +{person.subjects.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Office Information */}
                {(person.officeLocation || person.officeHours) && (
                  <div className="mt-4 pt-4 border-t border-gray-100 text-center">
                    {person.officeLocation && (
                      <p className="text-xs text-gray-500 mb-1">
                        Office: {person.officeLocation}
                      </p>
                    )}
                    {person.officeHours && (
                      <p className="text-xs text-gray-500">
                        Hours: {person.officeHours}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Show message when using mock data */}
        {leadership.length === 0 && (
          <div className="text-center mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-700">
              <strong>Note:</strong> Add staff members with leadership positions (Principal, Director, Department Head, etc.) 
              to display your actual leadership team here.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};